import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Tab,
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Pagination,
  Typography,
  Grid,
  InputAdornment,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox
} from '@mui/material';
import axios from 'axios';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { cyrillicToLatin } from 'src/helper/helperFunction';
import { API_BASE_URL } from 'src/apiConfig';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import {
  CategoryRadio,
  CategoryWrapper,
  HeaderTableCell,
  myBlue,
  StyledButton,
  StyledTableCell,
  StyledTableRow,
  StyledTabs,
  StyledTextField
} from './aditionalComponents';

/* 
  REZULTATI PO RAZREDIMA
  ADMIN STRANA GDE VIDI SVE REZULTATE PO RAZREDIMA + EXPORT
*/

const ResultsByGrade = () => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadExportAll, setLoadExportAll] = useState(false);
  const [loadExportFiltered, setLoadExportFiltered] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [finalStudentsMap, setFinalStudentsMap] = useState({});
  const [pendingStudentId, setPendingStudentId] = useState(null);
  const itemsPerPage = 10;

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setPage(1);
    setSearchTerm('');
  };

  useEffect(() => {
    setSelectedCategory('');
    setCategories([]);
    fetchResultsForGrade(selectedTab + 1);
    fetchFinalStudents();
    setSearchTerm('');
  }, [selectedTab]);

  const fetchResultsForGrade = async (grade) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/statistics.php?statistics=result_for_all_students&class=${grade}`
      );
      setResults(response.data.results || []);
      setFilteredResults(response.data.results || []);
      setTotalPages(Math.ceil(response.data.results.length / itemsPerPage));

      if (grade >= 9 && grade <= 12) {
        fetchCategories();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/categories.php`);
      const filteredCategories = response.data.filter((cat) => cat.id !== '0');
      setCategories(filteredCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFinalStudents = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/finalStudents.php?export`);
      const map = {};
      response.data.students.forEach(student => {
        map[String(student.id)] = true;
      });

      setFinalStudentsMap(map);
    } catch (error) {
      console.error(error);
    }
  };

  const handleFinalCheck = async (studentId) => {
    if (pendingStudentId !== null) return;
    setPendingStudentId(studentId);
    const isChecked = !!finalStudentsMap[String(studentId)];

    try {
      if (!isChecked) {
        // Dodavanje u final_students
        await axios.post(`${API_BASE_URL}/api/finalStudents.php`, { studentId });
      } else {
        // Brisanje iz final_students
        await axios.delete(`${API_BASE_URL}/api/finalStudents.php?id=${studentId}`);
      }

      await fetchFinalStudents();
    } catch (error) {
      console.error(error);
    } finally {
      setPendingStudentId(null);
    }
  };


  // Funkcija za filtriranje učenika na osnovu kategorije
  useEffect(() => {
    if (selectedCategory) {
      const filtered = results.filter(
        (student) => student.category === selectedCategory
      );

      setFilteredResults(filtered);
      setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    } else {
      setFilteredResults(results);
      setTotalPages(Math.ceil(results.length / itemsPerPage));
    }
  }, [selectedCategory, results]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };
  const exportAllToExcel = async () => {
    setLoadExportAll(true);

    const sheetNames = [
      'I OS',
      'II OS',
      'III OS',
      'IV OS',
      'V OS',
      'VI OS',
      'VII OS',
      'VIII OS',
      'I SS',
      'II SS',
      'III SS',
      'IV SS'
    ];

    const workbook = XLSX.utils.book_new();

    for (let classNum = 1; classNum <= 12; classNum++) {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/reviewAll.php?action=getClassData&class=${classNum}`
        );
        const classData = response.data || [];

        const cleanedClassData = classData.map(({ image_url, ...rest }) => ({
          ...rest,
          category: mapCategory(rest.category)
        }));

        const fieldNames = [
          'id',
          'student_name',
          'school_name',
          'location_name',
          'class',
          'category',
          'professor',
          'language',
          'point_sum'
        ];

        const worksheet = XLSX.utils.json_to_sheet(cleanedClassData, {
          header: fieldNames
        });

        const headerLabels = [
          'ID',
          'Име и презиме',
          'Назив школе',
          'Место',
          'Разред',
          'Категорија',
          'Професор',
          'Језик',
          'Поени'
        ];

        XLSX.utils.sheet_add_aoa(worksheet, [headerLabels], { origin: 'A1' });

        const sheetTitle = sheetNames[classNum - 1];
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle);
      } catch (error) {
        console.error(`Error fetching data for class ${classNum}`, error);
      }
    }

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', 'KonacniRezultati.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setLoadExportAll(false);
  };
  const exportFilteredToExcel = () => {
    setLoadExportFiltered(true);

    const cleanedFilteredResults = filteredResults.map(
      ({ image_url, ...rest }) => ({
        ...rest,
        category: mapCategory(rest.category)
      })
    );

    const fieldNames = [
      'studentId',
      'name',
      'school',
      'place',
      'class',
      'category',
      'professor',
      'language',
      'points'
    ];

    const worksheet = XLSX.utils.json_to_sheet(cleanedFilteredResults, {
      header: fieldNames
    });

    const headerLabels = [
      'ID',
      'Име и презиме',
      'Назив школе',
      'Место',
      'Разред',
      'Категорија',
      'Професор',
      'Језик',
      'Поени'
    ];

    XLSX.utils.sheet_add_aoa(worksheet, [headerLabels], { origin: 'A1' });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Филтрирани резултати');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', 'KonacniRezultati_Filtirani.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setLoadExportFiltered(false);
  };

  const mapCategory = (cat) => {
    switch (cat) {
      case '0':
        return 'A';
      case '1':
        return 'K1';
      case '2':
        return 'K2';
      case '3':
        return 'K3';
      default:
        return cat;
    }
  };

  const handleSearchChange = (e) => {
    const searchTerm = e.target.value;
    setSearchTerm(searchTerm);

    const normalizedSearchTerm = cyrillicToLatin(searchTerm.toLowerCase());

    const filtered = results.filter((student) => {
      const normalizedStudentName = cyrillicToLatin(student.name.toLowerCase());
      return normalizedStudentName.includes(normalizedSearchTerm);
    });

    setFilteredResults(filtered);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    setPage(1);
  };

  const handleViewWorkClick = (schoolId) => {
    const url = `https://imi.pmf.kg.ac.rs/kengur2/#/school/${schoolId}`;
    window.open(url, '_blank');
  };

  return (
    <Box sx={{ padding: '20px' }}>
      <Paper elevation={3} sx={{ padding: '20px', borderRadius: '12px' }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ marginBottom: '20px' }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 'bold', color: '#3B4C5A' }}
          >
            {t('result.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <StyledButton onClick={exportAllToExcel}>
              <DownloadIcon sx={{ marginRight: '10px' }} />
              {loadExportAll ? (
                <CircularProgress size={20} sx={{ marginRight: '10px' }} />
              ) : (
                t('result.exportExcel')
              )}
            </StyledButton>
            <StyledButton onClick={exportFilteredToExcel}>
              <DownloadIcon sx={{ marginRight: '10px' }} />
              {loadExportFiltered ? (
                <CircularProgress size={20} sx={{ marginRight: '10px' }} />
              ) : (
                t('result.exportFiltered')
              )}
            </StyledButton>
          </Box>
        </Grid>

        <StyledTextField
          fullWidth
          label={t('result.searchPlaceholder')}
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />

        <AppBar
          position="static"
          color="default"
          sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}
        >
          <StyledTabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Tabs for grades"
          >
            {[...Array(12).keys()].map((num) => (
              <Tab
                label={`${num + 1}. ${t('result.grade')}`}
                key={num}
                sx={{
                  fontWeight: selectedTab === num ? 'bold' : 'normal',
                  backgroundColor: selectedTab === num ? myBlue : '#ffffff',
                  color: selectedTab === num ? '#ffffff' : '#333',
                  '&:hover': {
                    backgroundColor: selectedTab === num ? '#1976D2' : '#E3F2FD'
                  },
                  borderRadius: '8px',
                  transition:
                    'background-color 0.3s, color 0.3s, box-shadow 0.3s'
                }}
              />
            ))}
          </StyledTabs>
        </AppBar>
        {/* Prikaži podkategorije samo za 9, 10, 11 i 12 razred */}
        {selectedTab >= 8 && (
          <CategoryWrapper>
            <RadioGroup
              value={selectedCategory}
              onChange={handleCategoryChange}
              row
            >
              <FormControlLabel
                key="all"
                value=""
                control={<Radio />}
                label={t('result.allCategories')}
              />
              {categories.map((category) => (
                <CategoryRadio
                  key={category.id}
                  value={category.id}
                  control={<Radio />}
                  label={category.name}
                />
              ))}
            </RadioGroup>
          </CategoryWrapper>
        )}
        <TableContainer
          component={Paper}
          sx={{ borderRadius: '12px', padding: '20px', marginTop: '20px' }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <HeaderTableCell>{t('result.name')}</HeaderTableCell>
                <HeaderTableCell>{t('result.school')}</HeaderTableCell>
                <HeaderTableCell>{t('result.place')}</HeaderTableCell>
                <HeaderTableCell>{t('result.category')}</HeaderTableCell>
                <HeaderTableCell>{t('result.points')}</HeaderTableCell>
                <HeaderTableCell></HeaderTableCell>
                <HeaderTableCell>{t('result.final')}</HeaderTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredResults
                .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                .map((row) => (
                  <StyledTableRow key={row.studentId}>
                    <StyledTableCell>{row.name}</StyledTableCell>
                    <StyledTableCell>{row.school}</StyledTableCell>
                    <StyledTableCell>{row.place}</StyledTableCell>
                    <StyledTableCell>{row.category}</StyledTableCell>

                    <StyledTableCell>{row.points}</StyledTableCell>
                    <StyledTableCell>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => handleViewWorkClick(row.schoolId)}
                      >
                        {t('result.viewWork')}
                      </Button>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Checkbox
                        checked={!!finalStudentsMap[String(row.studentId)]}
                        onChange={() => handleFinalCheck(row.studentId)}
                        disabled={pendingStudentId !== null}
                        color="primary"
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          sx={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}
        />
      </Paper>
    </Box>
  );
};

export default ResultsByGrade;
